import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const EMPTY = {
  clinical_total: 0,
  partner_total: 0,
  total: 0,
  clinical_by_consultation: {},
  partner_by_assignment: {},
};

const UnreadContext = createContext(EMPTY);
const POLL_MS = 30000;

/** Single poller for every unread badge in the app. */
export function UnreadProvider({ children }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY);

  const refresh = useCallback(async () => {
    if (!user) { setCounts(EMPTY); return; }
    try {
      const { data } = await api.get("/threads/unread");
      setCounts(data);
    } catch { /* badges are decorative — never surface a failure */ }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return;
    const t = setInterval(refresh, POLL_MS);
    const onRead = () => refresh();
    window.addEventListener("aura:thread-read", onRead);
    return () => { clearInterval(t); window.removeEventListener("aura:thread-read", onRead); };
  }, [user, refresh]);

  return (
    <UnreadContext.Provider value={{ ...counts, refresh }}>{children}</UnreadContext.Provider>
  );
}

export const useUnread = () => useContext(UnreadContext);

/** Call after marking a thread read so every badge re-syncs immediately. */
export const notifyThreadRead = () =>
  window.dispatchEvent(new CustomEvent("aura:thread-read"));
